import type {
  ComponentWithIconProps,
  DividerProps,
  LinkProps,
} from '@hover/blueprint';
import {
  Center,
  Icon,
  Link,
  Loader,
  Motion,
  Divider as SystemDivider,
  forwardRef,
  motion,
} from '@hover/blueprint';
import { iChevronRight } from '@hover/icons';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import type { UseTrackingProps } from 'src/shared/hooks/useTracking';
import { useTracking } from 'src/shared/hooks/useTracking';

const MenuLinkDivider = (props: DividerProps) => (
  <SystemDivider borderColor="neutral.200" margin="0" size="small" {...props} />
);

export interface MenuLinkProps
  extends LinkProps,
    Partial<ComponentWithIconProps>,
    UseTrackingProps {
  isLoading?: boolean;
}

const MotionLoader = motion(Loader);
const MotionIcon = motion(Icon);

const MenuLink = forwardRef<MenuLinkProps, 'a'>(
  (
    {
      children,
      icon,
      isLoading,
      buttonLocation,
      buttonText,
      optionalTrackingProperties,
      pageName,
      onClick,
      primaryCallToAction,
      track,
      ...props
    },
    ref,
  ) => {
    const { onClick: onClickWithTracking } = useTracking({
      buttonLocation,
      buttonText,
      optionalTrackingProperties,
      pageName,
      primaryCallToAction,
      track,
    });

    return (
      <Link
        _active={{ color: 'neutral.900 !important' }}
        _before={{}}
        _focusVisible={{ textDecorationColor: 'darkAlpha.400' }}
        _hover={{ color: 'neutral.900', textDecorationColor: 'neutral.900' }}
        color="neutral.900"
        cursor="pointer"
        data-group
        display="inline-flex"
        flexDirection="row"
        justifyContent="space-between"
        onClick={event => {
          onClickWithTracking(event);
          onClick?.(event);
        }}
        {...props}
        ref={ref}
      >
        {children}
        <Motion.Firm>
          <Center>
            <AnimatePresence initial={false}>
              {isLoading ? (
                <MotionLoader
                  animate={{ x: '0%', opacity: 1 }}
                  color="neutral.900"
                  exit={{ opacity: 0, x: '50%' }}
                  initial={{ x: '-50%', opacity: 0 }}
                  size="tiny"
                  sx={{ svg: { m: 0 } }}
                />
              ) : (
                <MotionIcon
                  animate={{ x: '0%', opacity: 1 }}
                  exit={{ opacity: 0, x: '50%' }}
                  icon={icon ?? iChevronRight}
                  initial={{ x: '-50%', opacity: 0 }}
                  isInline
                  size="small"
                  strokeWidth="2px"
                />
              )}
            </AnimatePresence>
          </Center>
        </Motion.Firm>
      </Link>
    );
  },
);

export { MenuLink, MenuLinkDivider };
